<template>
  <DatocmsStructuredText
    class="md:prose-md prose max-w-none lg:prose-lg"
    :data="data"
    :renderBlock="renderBlock"
    :renderInlineRecord="renderInlineRecord"
    :renderLinkToRecord="renderLinkToRecord"
  />
</template>

<script lang="ts">
import { StructuredText } from 'vue-datocms'

export default {
  components: {
    DatocmsStructuredText: StructuredText
  }
}
</script>

<script setup lang="ts">
const Button = resolveComponent('Button')
const HTML = resolveComponent('HTML')
const Image = resolveComponent('Image')
const ImageGallery = resolveComponent('ImageGallery')
const NuxtLink = resolveComponent('NuxtLink')
const Table = resolveComponent('Table')
const VideoEmbed = resolveComponent('VideoEmbed')
const Accordion = resolveComponent('Accordion')
const HighlightQuote = resolveComponent('HighlightQuote')
const HighlightImage = resolveComponent('HighlightImage')

const { localePathByType } = useLocalePathByType()

defineProps({
  data: {
    type: Object as {},
    default: () => {}
  }
})

function renderInlineRecord({ record }: { record: any }) {
  switch (record.__typename) {
    case 'PageRecord':
      return h(
        NuxtLink,
        { to: localePathByType(record.__typename, record.slug) },
        () => record.title
      )
    case 'ArticleRecord':
      return h(
        NuxtLink,
        {
          to: localePathByType(record.__typename, record.slug)
        },
        () => record.title
      )
    default:
      return null
  }
}

function renderLinkToRecord({ record, children, transformedMeta }: any) {
  switch (record.__typename) {
    case 'PageRecord':
      return h(
        NuxtLink,
        { ...transformedMeta, to: localePathByType(record.__typename, record.slug) },
        () => children
      )
    case 'ArticleRecord':
      return h(
        NuxtLink,
        {
          ...transformedMeta,
          to: localePathByType(record.__typename, record.slug)
        },
        () => children
      )
    default:
      return null
  }
}

function renderBlock({ record }: { record: any }) {
  switch (record.__typename) {
    case 'BlockButtonRecord':
      return h(Button, {
        label: record.label,
        variant: record.variant,
        to:
          record.reference !== null
            ? localePathByType(record.intLink.__typename, record.intLink.slug)
            : record.externalLink,
        target: record.externalLink && '_blank'
      })
    case 'BlockHtmlRecord':
      return h(HTML, {
        data: record
      })
    case 'ImageRecord':
      return h(Image, {
        src: record.image.url,
        srcSet: record.image.responsiveImage.webpSrcSet,
        width: record.image.width,
        height: record.image.height
      })
    case 'ImageGalleryRecord':
      return h(ImageGallery, {
        data: record.images,
        class: 'not-prose'
      })
    case 'VideoEmbedRecord':
      return h(VideoEmbed, {
        data: record.video,
        class: 'mb-12 lg:mb-20 w-full aspect-video'
      })
    case 'TableRecord':
      return h(Table, {
        data: record,
        class: '[&:not(:first-child)]:mt-10 [&:not(:first-child)]:md:mt-20]'
      })
    case 'AccordionListRecord':
      return h(Accordion, {
        data: record
      })
    case 'HighlightQuoteRecord':
      return h(HighlightQuote, {
        class: '!p-10 !lg-10 text-center !text-2xl !lg:text-3xl mb-9',
        data: record
      })
    case 'HighlightImageRecord':
      return h(HighlightImage, {
        class: 'mb-9',
        data: record
      })
    default:
      return null
  }
}
</script>
